/**
 * @fileoverview A etapa 2 da contratação (03/11/2026): os dados para a cobrança
 * e a forma de pagamento. Sem os dados, o formulário vem primeiro, no próprio
 * modal (antes, preencher fechava a contratação e perdia as escolhas); salvos,
 * viram um resumo com "Alterar" e o pagamento aparece. O cartão é tokenizado no
 * navegador: os campos ficam só aqui, e o número nunca vai para o Sttelar.
 */

import React from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ArrowLeft, Barcode, CreditCard, Lock, MailCheck, QrCode } from "lucide-react";

import BillingProfileFields, { fmtDocument } from "../../BillingProfileForm/BillingProfileFields";
import { RadioGroup } from "../PlanControls";
import { InfoTip } from "../../../pages/Flows/components/TriageManager/InfoTip";
import { useEmailConfirmation } from "../../../hooks/useEmailConfirmation";
import { i18n } from "../../../translate/i18n";
import { capitalize, money } from "../../../utils/billingPlans";
import { detectBrand, formatExpiry, onlyDigits } from "../../../utils/cardForm";
import { InternalChatRow } from "./CustomizeStep";
import { TrustNotes } from "./OrderSummary";

const t = (key, options) => i18n.t(`plansDialog.${key}`, options);
const tb = (key) => i18n.t(`billingProfile.${key}`);

const METHOD_ICON = { credit_card: CreditCard, pix: QrCode, boleto: Barcode };

const groupDigits = (value) => (onlyDigits(value).slice(0, 19).match(/.{1,4}/g) || []).join(" ");

/** O que falta na confirmação do e-mail: o servidor recusa salvar e contratar antes dela. */
const ConfirmEmailBox = ({ classes, email, onConfirmed }) => {
  const { check, resend, checking, sending, notice, error } = useEmailConfirmation({ email, onConfirmed });
  return (
    <div className={classes.emailBox}>
      <h4 className={classes.emailTitle}>
        <MailCheck aria-hidden="true" />
        {t("confirmEmail.title")}
      </h4>
      <p className={classes.emailText}>{t("confirmEmail.text", { email })}</p>
      {notice && (
        <p className={classes.emailNotice} role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className={classes.emailError} role="alert">
          {error}
        </p>
      )}
      <div className={classes.emailActions}>
        <button type="button" className={classes.secondary} onClick={check} disabled={checking}>
          {checking && <CircularProgress size={14} color="inherit" />}
          {i18n.t("aiSetup.confirmEmail.check")}
        </button>
        <button type="button" className={classes.textLink} onClick={resend} disabled={sending}>
          {i18n.t("aiSetup.confirmEmail.resend")}
        </button>
      </div>
    </div>
  );
};

const BillingSection = ({ classes, billing, open, onEdit, onCancelEdit }) => {
  const saved = billing.saved;
  // Com o cadastro existente e a leitura que falhou, o pagamento segue: o servidor usa o que está salvo.
  if (billing.loadFailed && !open && !saved) {
    return (
      <section className={classes.section} aria-labelledby="plans-billing-title">
        <h3 className={classes.sectionTitle} id="plans-billing-title">
          {tb("title")}
        </h3>
        <p className={classes.note} role="alert">
          {t("billingLoadFailed")}
        </p>
        <button type="button" className={classes.textLink} style={{ alignSelf: "flex-start" }} onClick={billing.reload}>
          {t("quoteRetry")}
        </button>
      </section>
    );
  }
  return (
    <section className={classes.section} aria-labelledby="plans-billing-title">
      <div className={classes.sectionHead}>
        <h3 className={classes.sectionTitle} id="plans-billing-title">
          {tb("title")}
        </h3>
        {saved && !billing.loading && (
          <button type="button" className={classes.textLink} onClick={open ? onCancelEdit : onEdit} disabled={billing.saving}>
            {open ? t("billingCancel") : t("billingEdit")}
          </button>
        )}
      </div>
      {billing.loading ? (
        <div className={classes.busyRow}>
          <CircularProgress size={15} color="inherit" />
          <span>{t("billingLoading")}</span>
        </div>
      ) : open ? (
        <>
          <p className={classes.billingIntro}>{t("billingIntro")}</p>
          <BillingProfileFields classes={classes} billing={billing} idPrefix="plans-billing" />
        </>
      ) : (
        saved && (
          <div className={classes.billingCard}>
            <span className={classes.billingName}>{saved.name}</span>
            <span>
              {saved.personType === "company" ? "CNPJ" : "CPF"} {fmtDocument(saved.document, saved.personType)}
            </span>
            <span>{saved.email}</span>
            <span>{t("billingAddress", { street: saved.street, number: saved.number, city: saved.city, state: saved.state })}</span>
          </div>
        )
      )}
    </section>
  );
};

const CardFields = ({ classes, card, onCardChange, errors, onBlur }) => {
  const brand = detectBrand(card.number);
  const field = (key) => ({
    id: `plans-card-${key}`,
    className: `${classes.input} ${errors[key] ? classes.inputError : ""}`,
    onBlur: () => onBlur(key),
    ...(errors[key] ? { "aria-invalid": true, "aria-describedby": `plans-card-${key}-error` } : {}),
  });
  const error = (key) =>
    errors[key] ? (
      <span className={classes.fieldError} id={`plans-card-${key}-error`} role="alert">
        {errors[key]}
      </span>
    ) : null;
  return (
    <div className={classes.cardGrid}>
      <div className={`${classes.field} ${classes.cardNumberCell}`}>
        <label className={classes.label} htmlFor="plans-card-number">
          {t("cardNumber")}
        </label>
        <div className={classes.inputWrap}>
          <input
            {...field("number")}
            className={`${field("number").className} ${classes.inputWithBrand}`}
            inputMode="numeric"
            autoComplete="cc-number"
            value={groupDigits(card.number)}
            onChange={(event) => onCardChange({ ...card, number: onlyDigits(event.target.value).slice(0, 19) })}
            placeholder="0000 0000 0000 0000"
          />
          {brand && <span className={classes.cardBrand}>{capitalize(brand)}</span>}
        </div>
        {error("number")}
      </div>
      <div className={`${classes.field} ${classes.cardHolderCell}`}>
        <label className={classes.label} htmlFor="plans-card-holder">
          {t("cardHolder")}
        </label>
        <input {...field("holder")} autoComplete="cc-name" value={card.holder} onChange={(event) => onCardChange({ ...card, holder: event.target.value.toUpperCase().slice(0, 64) })} />
        {error("holder")}
      </div>
      <div className={`${classes.field} ${classes.cardExpiryCell}`}>
        <label className={classes.label} htmlFor="plans-card-expiry">
          {t("cardExpiry")}
        </label>
        <input
          {...field("expiry")}
          inputMode="numeric"
          autoComplete="cc-exp"
          value={formatExpiry(card.expiry)}
          onChange={(event) => onCardChange({ ...card, expiry: onlyDigits(event.target.value).slice(0, 4) })}
          placeholder={t("cardExpiryPlaceholder")}
        />
        {error("expiry")}
      </div>
      <div className={`${classes.field} ${classes.cardCvvCell}`}>
        <span className={classes.labelRow}>
          <label className={classes.label} htmlFor="plans-card-cvv">
            {t("cardCvv")}
          </label>
          <InfoTip text={t("cvvHelp")} />
        </span>
        <input {...field("cvv")} inputMode="numeric" autoComplete="cc-csc" value={card.cvv} onChange={(event) => onCardChange({ ...card, cvv: onlyDigits(event.target.value).slice(0, 4) })} placeholder="000" />
        {error("cvv")}
      </div>
      <p className={classes.cardNote} style={{ margin: 0 }}>
        <Lock aria-hidden="true" />
        <span>{t("cardSecure")}</span>
      </p>
    </div>
  );
};

const PaymentSection = ({ classes, titleRef, changeMode, savedCardName, method, onMethodChange, boletoAllowed, boletoBlock, boletoMinCents, quote, card, onCardChange, cardErrors, onCardBlur }) => {
  const options = changeMode
    ? [
        // Sem cartão salvo (a assinatura no boleto), a opção do cartão nem aparece.
        ...(savedCardName ? [{ value: "credit_card", label: savedCardName }] : []),
        { value: "pix", label: t("pix") },
        { value: "boleto", label: t("boleto"), disabled: !boletoAllowed },
      ]
    : [
        { value: "credit_card", label: t("card") },
        { value: "boleto", label: t("boleto"), disabled: !boletoAllowed },
      ];
  return (
    <section className={classes.section} aria-labelledby="plans-dialog-method">
      <h3 className={classes.sectionTitle} id="plans-dialog-method" ref={titleRef} tabIndex={-1}>
        {t("payment")}
      </h3>
      <RadioGroup
        className={classes.methods}
        labelledBy="plans-dialog-method"
        options={options}
        value={method}
        onChange={onMethodChange}
        renderOption={({ value, label, disabled }, props) => {
          const Icon = METHOD_ICON[value];
          return (
            <button type="button" className={`${classes.method} ${value === method ? classes.methodSelected : ""} ${disabled ? classes.methodDisabled : ""}`} {...props}>
              <Icon aria-hidden="true" />
              {label}
            </button>
          );
        }}
      />
      {boletoBlock && (!changeMode || quote) && (
        <p className={classes.note}>
          {!changeMode ? t("boletoMin", { min: money(boletoMinCents) }) : boletoBlock === "renewal" ? t("boletoRenewal") : t("boletoMinChange", { min: money(boletoMinCents) })}
        </p>
      )}
      {changeMode ? (
        <p className={classes.note}>{method === "pix" ? t("pixNote") : method === "boleto" ? t("boletoDue") : t("savedCardNote")}</p>
      ) : method === "credit_card" ? (
        <CardFields classes={classes} card={card} onCardChange={onCardChange} errors={cardErrors} onBlur={onCardBlur} />
      ) : (
        <p className={classes.note}>{t("boletoDue")}</p>
      )}
    </section>
  );
};

const PaymentStep = ({
  classes,
  titleRef,
  paymentTitleRef,
  internalOnly,
  catalog,
  internalHelp,
  internalChatSeats,
  minInternal,
  maxInternal,
  onInternalChange,
  onBack,
  emailPending,
  email,
  onEmailConfirmed,
  showBilling,
  billing,
  billingOpen,
  onEditBilling,
  onCancelEditBilling,
  showPayment,
  freeChange,
  paymentProps,
  compact,
  notes,
}) => (
  <div className={classes.stepContent}>
    {internalOnly ? (
      <section className={classes.section} aria-labelledby="plans-dialog-internal">
        <h3 className={classes.sectionTitle} id="plans-dialog-internal" ref={titleRef} tabIndex={-1}>
          {t("howManyInternal")}
        </h3>
        <div className={classes.rows}>
          <InternalChatRow classes={classes} catalog={catalog} help={internalHelp} value={internalChatSeats} min={minInternal} max={maxInternal} onChange={onInternalChange} />
        </div>
      </section>
    ) : (
      <>
        {/* Só para leitores de tela: ao avançar, o foco vem aqui e a mudança de etapa é anunciada. */}
        <h3 className={classes.srOnly} ref={titleRef} tabIndex={-1}>
          {t("stepPayment")}
        </h3>
        <button type="button" className={classes.backLink} onClick={onBack}>
          <ArrowLeft aria-hidden="true" />
          {t("back")}
        </button>
      </>
    )}
    {emailPending ? (
      <ConfirmEmailBox classes={classes} email={email} onConfirmed={onEmailConfirmed} />
    ) : (
      <>
        {showBilling && <BillingSection classes={classes} billing={billing} open={billingOpen} onEdit={onEditBilling} onCancelEdit={onCancelEditBilling} />}
        {showPayment && <PaymentSection classes={classes} titleRef={paymentTitleRef} {...paymentProps} />}
        {freeChange && !billingOpen && (
          <section className={classes.section} aria-labelledby="plans-free-change">
            <h3 className={classes.sectionTitle} id="plans-free-change" ref={paymentTitleRef} tabIndex={-1}>
              {t("freeChangeTitle")}
            </h3>
            <p className={classes.note}>{t("freeChangeText")}</p>
          </section>
        )}
      </>
    )}
    {compact && notes.length > 0 && !billingOpen && !emailPending && <TrustNotes classes={classes} notes={notes} className={classes.trustInline} />}
  </div>
);

export default PaymentStep;
